// Banner Card Height Matching
// Sets promo media height to match the tallest product image area (.image-cont) in the grid

(function() {
  // Prevent multiple executions
  if (window.bannerCardHeightsInitialized) return;
  window.bannerCardHeightsInitialized = true;

let isCalculating = false;
let isScrolling = false;
let scrollTimeout;

// Track scrolling state
window.addEventListener('scroll', () => {
  isScrolling = true;
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    isScrolling = false;
  }, 150);
}, { passive: true });

function isPromoVisibleForViewport(banner) {
  const w = window.innerWidth;
  if (banner.classList.contains('banner-card--hide-desktop') && w >= 700) {
    return false;
  }
  if (banner.classList.contains('banner-card--hide-mobile') && w <= 699) {
    return false;
  }
  return true;
}

function readThemeProductColumnCount(list) {
  const raw = getComputedStyle(list).getPropertyValue('--product-columns-desktop').trim();
  const n = parseInt(raw, 10);
  if (n > 0 && n <= 24) {
    return n;
  }
  return 0;
}

function inferColumnCountFromLayout(list, allCards) {
  const listWidth = list.offsetWidth;
  const firstCard = allCards.find(card => !card.classList.contains('banner-card'));
  if (!firstCard) return 1;
  const cardWidth = firstCard.getBoundingClientRect().width;
  const columnGap = parseFloat(getComputedStyle(list).columnGap) || 0;
  const estimate = Math.round((listWidth + columnGap) / (cardWidth + columnGap));
  return Math.max(1, Math.min(24, estimate));
}

function clearPromoGridAdjustments(list) {
  list.removeAttribute('data-explicit-grid');
  list.style.removeProperty('--explicit-grid-columns');
  list.querySelectorAll('.product-block').forEach(card => {
    card.style.gridColumn = '';
    card.style.gridRow = '';
    card.classList.remove('banner-card--pushed');
    if (!card.classList.contains('banner-card')) {
      card.style.height = '';
    }
  });
  list.querySelectorAll('.banner-card .banner-card__figure').forEach(fig => {
    fig.style.height = '';
  });
}

function adjustBannerPositions() {
  const productGrids = document.querySelectorAll('.product-grid');

  productGrids.forEach(list => {
    const allCards = Array.from(list.querySelectorAll('.product-block'));
    if (allCards.length === 0) return;

    allCards.forEach(card => {
      if (card.classList.contains('banner-card') && !isPromoVisibleForViewport(card)) {
        card.style.gridColumn = '';
        card.style.gridRow = '';
        card.classList.remove('banner-card--pushed');
      }
    });

    const layoutCards = allCards.filter(card => {
      if (!card.classList.contains('banner-card')) return true;
      return isPromoVisibleForViewport(card);
    });

    const visibleBanners = layoutCards.filter(c => c.classList.contains('banner-card'));
    if (visibleBanners.length === 0) {
      clearPromoGridAdjustments(list);
      return;
    }

    const firstCard = layoutCards.find(card => !card.classList.contains('banner-card'));
    if (!firstCard) return;

    // Use the theme's column count so explicit grid positions match real CSS grid (width
    // rounding often yields an off-by-one and visually shuffles items out of DOM order).
    let columnsPerRow = readThemeProductColumnCount(list);
    if (!columnsPerRow) {
      columnsPerRow = inferColumnCountFromLayout(list, layoutCards);
    }

    if (columnsPerRow <= 2) {
      // Reset all adjustments for small layouts
      list.removeAttribute('data-explicit-grid');
      list.style.removeProperty('--explicit-grid-columns');
      allCards.forEach(card => {
        card.style.gridColumn = '';
        card.style.gridRow = '';
        card.classList.remove('banner-card--pushed');
      });
      return;
    }

    // Enable explicit grid mode
    list.setAttribute('data-explicit-grid', 'true');
    list.style.setProperty('--explicit-grid-columns', columnsPerRow);

    // Track which positions are filled in the grid
    const grid = {}; // grid[row][column] = true if filled

    // Helper to find next available position
    function findNextPosition(row, column, span) {
      while (true) {
        // Check if current position can fit the span
        let canFit = true;
        if (column + span - 1 > columnsPerRow) {
          canFit = false;
        } else {
          for (let c = column; c < column + span; c++) {
            if (grid[row] && grid[row][c]) {
              canFit = false;
              break;
            }
          }
        }

        if (canFit) {
          return { row, column };
        }

        // Move to next column
        column++;
        if (column > columnsPerRow) {
          row++;
          column = 1;
        }
      }
    }

    // Helper to mark positions as filled
    function markFilled(row, column, span) {
      if (!grid[row]) grid[row] = {};
      for (let c = column; c < column + span; c++) {
        grid[row][c] = true;
      }
    }

    // Position all cards - using dense packing algorithm (only promos visible on this viewport)
    layoutCards.forEach((card) => {
      // Reset any previous adjustments
      card.classList.remove('banner-card--pushed');

      // Determine card span
      const isMobile = window.innerWidth <= 699;
      let cardSpan = 1;

      if (isMobile) {
        if (card.classList.contains('banner-card--mobile-span-2')) {
          cardSpan = 2;
        }
      } else {
        if (card.classList.contains('banner-card--desktop-span-2')) {
          cardSpan = 2;
        }
      }

      // Always search from row 1, col 1 to find the first available position
      // This implements "dense" packing that fills gaps
      const pos = findNextPosition(1, 1, cardSpan);

      // Apply position
      card.style.gridColumn = `${pos.column} / span ${cardSpan}`;
      card.style.gridRow = pos.row;

      // Mark as filled
      markFilled(pos.row, pos.column, cardSpan);

      // Mark pushed banners
      if (cardSpan === 2 && pos.column === 1) {
        card.classList.add('banner-card--pushed');
      }
    });
  });
}

function matchCardHeights() {
  // Prevent concurrent calculations
  if (isCalculating) return;

  // Skip during active scrolling to prevent jumps
  if (isScrolling) {
    setTimeout(matchCardHeights, 200);
    return;
  }

  isCalculating = true;

  // First, adjust banner positions to prevent empty spaces
  adjustBannerPositions();

  const productGrids = document.querySelectorAll('.product-grid');

  productGrids.forEach(list => {
    const bannerCards = Array.from(list.querySelectorAll('.banner-card')).filter(isPromoVisibleForViewport);
    if (bannerCards.length === 0) {
      clearPromoGridAdjustments(list);
      return;
    }

    const productCards = list.querySelectorAll('.product-block:not(.banner-card)');

    if (productCards.length === 0) {
      return;
    }

    let sampleImageCont = null;
    for (let i = 0; i < productCards.length; i += 1) {
      const el = productCards[i].querySelector('.image-cont');
      if (el) {
        sampleImageCont = el;
        break;
      }
    }
    if (!sampleImageCont || sampleImageCont.offsetHeight === 0) {
      isCalculating = false;
      setTimeout(matchCardHeights, 50);
      return;
    }

    // Store current scroll position
    const scrollY = window.scrollY || window.pageYOffset;

    // Reset inline styles first
    productCards.forEach(card => {
      card.style.height = '';
    });
    bannerCards.forEach(banner => {
      const figure = banner.querySelector('.banner-card__figure');
      if (figure) figure.style.height = '';
    });

    let maxImageHeight = 0;

    productCards.forEach(card => {
      const imageCont = card.querySelector('.image-cont');
      if (!imageCont) return;
      const h = imageCont.offsetHeight;
      if (h > maxImageHeight) {
        maxImageHeight = h;
      }
    });

    if (maxImageHeight <= 0) {
      return;
    }

    bannerCards.forEach(banner => {
      const figure = banner.querySelector('.banner-card__figure');
      if (!figure) return;

      let scale = parseFloat(getComputedStyle(banner).getPropertyValue('--banner-video-height-scale'));
      if (Number.isNaN(scale) || scale <= 0) {
        scale = 1;
      }
      scale = Math.min(3, Math.max(0.25, scale));

      const heightPx = Math.round(maxImageHeight * scale);
      const newHeight = `${heightPx}px`;

      if (figure.style.height !== newHeight) {
        figure.style.height = newHeight;
      }
    });

    // Restore scroll position to prevent jump
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - scrollY) > 5) {
        window.scrollTo(0, scrollY);
      }
    });
  });

  isCalculating = false;
}

// Run as soon as possible
function initBannerCards() {
  // Use requestAnimationFrame to ensure layout is calculated
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      matchCardHeights();
    });
  });
}

// Run on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBannerCards);
} else {
  initBannerCards();
}

// Run on window resize with debounce
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(matchCardHeights, 150);
});

// Run when images load
window.addEventListener('load', matchCardHeights);

// Handle pagination navigation
window.addEventListener('popstate', () => {
  setTimeout(matchCardHeights, 100);
});

// Handle Shopify section rendering
document.addEventListener('shopify:section:load', matchCardHeights);
document.addEventListener('shopify:section:reorder', matchCardHeights);

// Run when new content is added (for AJAX pagination)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    let shouldRecalculate = false;
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList && (node.classList.contains('product-block') || node.classList.contains('banner-card'))) {
              shouldRecalculate = true;
            } else if (node.querySelector && (node.querySelector('.product-block') || node.querySelector('.banner-card'))) {
              shouldRecalculate = true;
            }
          }
        });
      }
    });
    if (shouldRecalculate) {
      setTimeout(matchCardHeights, 50);
    }
  });

  // Observe the entire body for content changes
  observer.observe(document.body, { childList: true, subtree: true });

  // Also observe product grids specifically
  setTimeout(() => {
    document.querySelectorAll('.product-grid').forEach(list => {
      observer.observe(list, { childList: true, subtree: true });
    });
  }, 100);
}

})(); // End IIFE
